import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Trash2, Save, Search, Package, Eye, X, FileText, Upload, Image, Paperclip } from 'lucide-react';
import { formatCurrency, formatDateTimeDR, getLocalDateString, formatDateDR } from '../utils/formatters';

const Purchases = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewPurchaseDialog, setShowNewPurchaseDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productCodeInput, setProductCodeInput] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showAttachmentPreview, setShowAttachmentPreview] = useState(false);
  const fileInputRef = useRef(null);

  // Filtros de fecha
  const hoy = getLocalDateString();
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  // Datos generales de la compra
  const [purchaseData, setPurchaseData] = useState({
    numero_factura: '',
    numero_conduce: '',
    ncf: '',
    suplidor_id: '',
    suplidor_nombre: '',
    fecha_factura: hoy,
    fecha_vencimiento: '',
    moneda: 'DOP',
    forma_pago: 'EFECTIVO',
    transporte: 0,
    descuento: 0,
    observaciones: '',
  });

  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
    fetchPurchases();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Error al cargar suplidores');
    }
  };

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${API}/purchases`);
      setPurchases(response.data);
    } catch (error) {
      toast.error('Error al cargar compras');
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierSelect = (supplierId) => {
    if (supplierId === 'none') {
      setPurchaseData({
        ...purchaseData,
        suplidor_id: '',
        suplidor_nombre: ''
      });
    } else {
      const supplier = suppliers.find(s => s.id === supplierId);
      if (supplier) {
        setPurchaseData({
          ...purchaseData,
          suplidor_id: supplier.id,
          suplidor_nombre: supplier.nombre
        });
      }
    }
  };

  const filteredProducts = products.filter(p =>
    p.activo !== false && (
      p.nombre.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
      p.codigo_interno?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
      (p.codigo_barras && (Array.isArray(p.codigo_barras) ? p.codigo_barras.some(b => b.toLowerCase().includes(productSearchTerm.toLowerCase())) : p.codigo_barras.toLowerCase().includes(productSearchTerm.toLowerCase()))) ||
      p.categoria?.toLowerCase().includes(productSearchTerm.toLowerCase())
    )
  );

  const handleProductCodeSubmit = (e) => {
    e.preventDefault();
    if (!productCodeInput.trim()) return;

    const product = products.find(p =>
      p.codigo_interno === productCodeInput.trim() ||
      (Array.isArray(p.codigo_barras) ? p.codigo_barras.includes(productCodeInput.trim()) : p.codigo_barras === productCodeInput.trim())
    );

    if (product) {
      addToCart(product);
      setProductCodeInput('');
      toast.success(`${product.nombre} agregado`);
    } else {
      setProductSearchTerm(productCodeInput.trim());
      setProductSearchOpen(true);
      setProductCodeInput('');
      toast.info('Producto no encontrado. Abriendo busqueda...');
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.producto_id === product.id);
    // Use costo field (from Product model) or precio_compra as fallback
    const productCost = product.costo || product.precio_compra || 0;

    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.producto_id === product.id
            ? { ...item, cantidad: item.cantidad + 1, subtotal: (item.cantidad + 1) * item.costo_unitario }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          producto_id: product.id,
          producto_nombre: product.nombre,
          cantidad: 1,
          costo_unitario: productCost,
          subtotal: productCost
        }
      ]);
    }
  };

  const updateCartItem = (index, field, value) => {
    const newCart = [...cart];
    newCart[index][field] = parseFloat(value) || 0;

    if (field === 'cantidad' || field === 'costo_unitario') {
      newCart[index].subtotal = newCart[index].cantidad * newCart[index].costo_unitario;
    }

    setCart(newCart);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
    const total = subtotal + parseFloat(purchaseData.transporte || 0) - parseFloat(purchaseData.descuento || 0);
    return { subtotal, total };
  };

  // File attachment handling
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten archivos JPG, PNG, WebP o PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no debe exceder 10MB');
      return;
    }

    setAttachmentFile(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setAttachmentPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadAttachment = async () => {
    if (!attachmentFile) return '';
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', attachmentFile);
      const response = await axios.post(`${API}/uploads/purchase-attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.url;
    } catch (error) {
      toast.error('Error al subir el archivo adjunto');
      return '';
    } finally {
      setUploading(false);
    }
  };

  const handleSavePurchase = async () => {
    if (cart.length === 0) {
      toast.error('Agregue al menos un producto');
      return;
    }

    if (!purchaseData.numero_factura.trim()) {
      toast.error('Ingrese el numero de factura');
      return;
    }

    setLoading(true);
    try {
      let adjunto_url = '';
      if (attachmentFile) {
        adjunto_url = await uploadAttachment();
      }

      const payload = {
        ...purchaseData,
        items: cart,
        transporte: parseFloat(purchaseData.transporte) || 0,
        descuento: parseFloat(purchaseData.descuento) || 0,
        fecha_factura: new Date(purchaseData.fecha_factura).toISOString(),
        fecha_vencimiento: purchaseData.fecha_vencimiento ? new Date(purchaseData.fecha_vencimiento).toISOString() : null,
        adjunto_url,
      };

      await axios.post(`${API}/purchases`, payload);

      toast.success('Compra registrada exitosamente');
      setShowNewPurchaseDialog(false);
      resetForm();
      fetchPurchases();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar la compra');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCart([]);
    setPurchaseData({
      numero_factura: '',
      numero_conduce: '',
      ncf: '',
      suplidor_id: '',
      suplidor_nombre: '',
      fecha_factura: getLocalDateString(),
      fecha_vencimiento: '',
      moneda: 'DOP',
      forma_pago: 'EFECTIVO',
      transporte: 0,
      descuento: 0,
      observaciones: '',
    });
    setProductCodeInput('');
    setProductSearchTerm('');
    removeAttachment();
  };

  const viewPurchaseDetail = async (purchaseId) => {
    try {
      const response = await axios.get(`${API}/purchases/${purchaseId}`);
      setSelectedPurchase(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Error al cargar el detalle de la compra');
    }
  };

  const handleDeletePurchase = async (purchaseId) => {
    if (window.confirm('¿Esta seguro de eliminar esta compra? Esta accion no se puede deshacer.')) {
      try {
        await axios.delete(`${API}/purchases/${purchaseId}`);
        toast.success('Compra eliminada');
        fetchPurchases();
        fetchProducts();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Error al eliminar');
      }
    }
  };

  // Filter purchases by date and search
  const filteredPurchases = purchases.filter((p) => {
    const matchesSearch =
      !searchTerm ||
      p.numero_factura?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.suplidor_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.ncf?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.usuario_nombre?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesDate = true;
    if (fechaDesde || fechaHasta) {
      const purchaseDate = new Date(p.fecha_factura).toISOString().split('T')[0];
      if (fechaDesde && purchaseDate < fechaDesde) matchesDate = false;
      if (fechaHasta && purchaseDate > fechaHasta) matchesDate = false;
    }

    return matchesSearch && matchesDate;
  });

  const { subtotal, total } = calculateTotals();

  const canDelete = user?.role === 'superadmin';

  if (loading && purchases.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="purchases-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Compras</h1>
          <p className="text-slate-600 mt-1">Registro de compras y entradas de inventario</p>
        </div>
        <Dialog open={showNewPurchaseDialog} onOpenChange={(open) => { setShowNewPurchaseDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="create-purchase-btn">
              <Plus className="mr-2" size={18} />
              Nueva Compra
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto" data-testid="create-purchase-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl">Registrar Nueva Compra</DialogTitle>
              <DialogDescription>Ingrese los datos de la compra y seleccione los productos</DialogDescription>
            </DialogHeader>

            {/* ENCABEZADO DE COMPRA */}
            <div className="bg-slate-50 p-4 rounded-lg space-y-4 border border-slate-200">
              <h3 className="font-semibold text-lg text-slate-800 border-b pb-2">Datos de la Compra</h3>

              <div className="grid grid-cols-4 gap-4">
                {/* Suplidor */}
                <div className="col-span-2">
                  <Label>Suplidor</Label>
                  <Select
                    value={purchaseData.suplidor_id || 'none'}
                    onValueChange={handleSupplierSelect}
                  >
                    <SelectTrigger data-testid="supplier-select">
                      <SelectValue placeholder="Seleccionar suplidor..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin suplidor / Manual</SelectItem>
                      {suppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* No. Factura */}
                <div>
                  <Label>No. Factura *</Label>
                  <Input
                    value={purchaseData.numero_factura}
                    onChange={(e) => setPurchaseData({ ...purchaseData, numero_factura: e.target.value })}
                    placeholder="Ej: FAC-001"
                    data-testid="purchase-numero-factura"
                  />
                </div>

                {/* Fecha Factura */}
                <div>
                  <Label>Fecha Factura *</Label>
                  <Input
                    type="date"
                    value={purchaseData.fecha_factura}
                    onChange={(e) => setPurchaseData({ ...purchaseData, fecha_factura: e.target.value })}
                    data-testid="purchase-fecha"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                {/* Nombre Suplidor (manual) */}
                <div>
                  <Label>Nombre Suplidor</Label>
                  <Input
                    value={purchaseData.suplidor_nombre}
                    onChange={(e) => setPurchaseData({ ...purchaseData, suplidor_nombre: e.target.value })}
                    placeholder="Nombre del suplidor"
                    disabled={!!purchaseData.suplidor_id}
                  />
                </div>

                {/* No. Conduce */}
                <div>
                  <Label>No. Conduce</Label>
                  <Input
                    value={purchaseData.numero_conduce}
                    onChange={(e) => setPurchaseData({ ...purchaseData, numero_conduce: e.target.value })}
                    placeholder="Opcional"
                  />
                </div>

                {/* NCF */}
                <div>
                  <Label>NCF</Label>
                  <Input
                    value={purchaseData.ncf}
                    onChange={(e) => setPurchaseData({ ...purchaseData, ncf: e.target.value })}
                    placeholder="Opcional"
                  />
                </div>

                {/* Fecha Vencimiento */}
                <div>
                  <Label>Fecha Vencimiento</Label>
                  <Input
                    type="date"
                    value={purchaseData.fecha_vencimiento}
                    onChange={(e) => setPurchaseData({ ...purchaseData, fecha_vencimiento: e.target.value })}
                  />
                </div>
              </div>

              {/* Moneda y Forma de Pago */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Moneda</Label>
                  <Select value={purchaseData.moneda} onValueChange={(value) => setPurchaseData({ ...purchaseData, moneda: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DOP">DOP (Peso Dominicano)</SelectItem>
                      <SelectItem value="USD">USD (Dolar)</SelectItem>
                      <SelectItem value="EUR">EUR (Euro)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Forma de Pago *</Label>
                  <Select value={purchaseData.forma_pago} onValueChange={(value) => setPurchaseData({ ...purchaseData, forma_pago: value })}>
                    <SelectTrigger data-testid="purchase-forma-pago">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EFECTIVO">Efectivo</SelectItem>
                      <SelectItem value="CHEQUES/TRANSFERENCIA/DEPOSITO">Cheques/Transferencia/Deposito</SelectItem>
                      <SelectItem value="TARJETA CRÉDITO/DÉBITO">Tarjeta Credito/Debito</SelectItem>
                      <SelectItem value="COMPRA A CREDITO">Compra a Credito</SelectItem>
                      <SelectItem value="PERMUTA">Permuta</SelectItem>
                      <SelectItem value="NOTA DE CREDITO">Nota de Credito</SelectItem>
                      <SelectItem value="MIXTO">Mixto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Adjuntar Archivo */}
                <div>
                  <Label>Adjunto (Foto/PDF)</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="purchase-file-input"
                  />
                  {!attachmentFile ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start text-slate-500 hover:text-slate-700"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="purchase-attach-btn"
                    >
                      <Paperclip size={16} className="mr-2" />
                      Seleccionar archivo...
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                      {attachmentFile.type === 'application/pdf' ? (
                        <FileText size={18} className="text-red-500 flex-shrink-0" />
                      ) : (
                        <Image size={18} className="text-blue-500 flex-shrink-0" />
                      )}
                      <span className="text-sm truncate flex-1">{attachmentFile.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removeAttachment}
                        className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Preview de imagen adjunta */}
              {attachmentPreview && (
                <div className="mt-2">
                  <img
                    src={attachmentPreview}
                    alt="Vista previa"
                    className="max-h-32 rounded-lg border border-slate-200 object-contain"
                  />
                </div>
              )}
            </div>

            {/* SELECCION DE PRODUCTOS */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-slate-800 border-b pb-2">Seleccion de Productos</h3>

              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label htmlFor="product-code-purchase">Codigo Interno / Codigo de Barras</Label>
                  <form onSubmit={handleProductCodeSubmit} className="flex gap-2">
                    <Input
                      id="product-code-purchase"
                      value={productCodeInput}
                      onChange={(e) => setProductCodeInput(e.target.value)}
                      placeholder="Escriba o escanee el codigo..."
                      className="flex-1"
                      data-testid="purchase-product-code"
                    />
                    <Button type="submit" variant="outline">Agregar</Button>
                  </form>
                </div>
                <Button
                  type="button"
                  onClick={() => setProductSearchOpen(true)}
                  className="bg-blue-500 hover:bg-blue-600"
                  data-testid="purchase-search-products-btn"
                >
                  <Search size={16} className="mr-2" />
                  Busqueda Avanzada
                </Button>
              </div>
            </div>

            {/* TABLA DE PRODUCTOS EN COMPRA */}
            <div className="space-y-3">
              <h3 className="font-semibold text-lg text-slate-800 flex items-center gap-2">
                <ShoppingCart size={20} />
                Productos en Compra ({cart.length})
              </h3>

              {cart.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
                  <ShoppingCart size={48} className="mx-auto text-slate-400 mb-3" />
                  <p className="text-slate-600">No hay productos agregados</p>
                  <p className="text-sm text-slate-500 mt-1">Use el campo de codigo o busqueda avanzada</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 border-b">
                      <tr>
                        <th className="text-left p-2 font-semibold">Cod. Interno</th>
                        <th className="text-left p-2 font-semibold">Producto</th>
                        <th className="text-center p-2 font-semibold w-24">Cantidad</th>
                        <th className="text-right p-2 font-semibold">Costo Unit.</th>
                        <th className="text-right p-2 font-semibold">Subtotal</th>
                        <th className="text-center p-2 font-semibold">Stock Actual</th>
                        <th className="text-center p-2 font-semibold w-20">Accion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cart.map((item, index) => {
                        const producto = products.find(p => p.id === item.producto_id);
                        return (
                          <tr key={item.producto_id} className="border-b hover:bg-slate-50" data-testid={`purchase-cart-item-${index}`}>
                            <td className="p-2 text-slate-700">{producto?.codigo_interno || '-'}</td>
                            <td className="p-2">
                              <div className="font-medium text-slate-800">{item.producto_nombre}</div>
                              <div className="text-xs text-slate-500">{producto?.unidad_medida}</div>
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                min="1"
                                value={item.cantidad}
                                onChange={(e) => updateCartItem(index, 'cantidad', e.target.value)}
                                className="w-20 h-8 text-center mx-auto"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                value={item.costo_unitario}
                                onChange={(e) => updateCartItem(index, 'costo_unitario', e.target.value)}
                                step="0.01"
                                className="w-28 h-8 text-right ml-auto"
                              />
                            </td>
                            <td className="p-2 text-right font-bold text-green-600">{formatCurrency(item.subtotal)}</td>
                            <td className="p-2 text-center">
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                                {producto?.existencia || 0}
                              </span>
                            </td>
                            <td className="p-2 text-center">
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => removeFromCart(index)}
                                className="h-7 w-7 p-0 hover:bg-red-100 hover:text-red-600"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* OBSERVACIONES */}
            <div className="space-y-2">
              <Label htmlFor="purchase-obs">Observaciones / Notas</Label>
              <textarea
                id="purchase-obs"
                className="w-full border rounded-lg p-2 text-sm resize-none"
                rows={3}
                value={purchaseData.observaciones}
                onChange={(e) => setPurchaseData({ ...purchaseData, observaciones: e.target.value })}
                placeholder="Notas adicionales..."
              />
            </div>

            {/* TOTALES */}
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-6 rounded-lg border-2 border-slate-300">
              <h3 className="font-semibold text-lg text-slate-800 mb-4 border-b pb-2">Resumen de Compra</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <Label>+ Transporte</Label>
                    <Input
                      type="number"
                      value={purchaseData.transporte}
                      onChange={(e) => setPurchaseData({ ...purchaseData, transporte: e.target.value })}
                      step="0.01"
                    />
                  </div>
                  <div>
                    <Label>- Descuento</Label>
                    <Input
                      type="number"
                      value={purchaseData.descuento}
                      onChange={(e) => setPurchaseData({ ...purchaseData, descuento: e.target.value })}
                      step="0.01"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-base">
                    <span className="text-slate-700">Subtotal:</span>
                    <span className="font-semibold text-slate-900">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-emerald-700">
                    <span>+ Transporte:</span>
                    <span>{formatCurrency(parseFloat(purchaseData.transporte) || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-red-700">
                    <span>- Descuento:</span>
                    <span>{formatCurrency(parseFloat(purchaseData.descuento) || 0)}</span>
                  </div>
                  <div className="border-t-2 border-slate-400 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-slate-900">TOTAL:</span>
                      <span className="text-3xl font-bold text-green-600">{formatCurrency(total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowNewPurchaseDialog(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSavePurchase}
                disabled={loading || cart.length === 0 || uploading}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-lg py-6"
                data-testid="submit-purchase-btn"
              >
                <Save className="mr-2" size={20} />
                {uploading ? 'Subiendo adjunto...' : loading ? 'Guardando...' : `Guardar Compra ${cart.length > 0 ? `(${formatCurrency(total)})` : ''}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtros de Rango de Fecha */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="fecha-desde-compra" className="text-sm font-medium text-slate-700 mb-1 block">
              Fecha Desde
            </Label>
            <Input
              id="fecha-desde-compra"
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="w-full"
              data-testid="purchase-fecha-desde"
            />
          </div>
          <div>
            <Label htmlFor="fecha-hasta-compra" className="text-sm font-medium text-slate-700 mb-1 block">
              Fecha Hasta
            </Label>
            <Input
              id="fecha-hasta-compra"
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="w-full"
              data-testid="purchase-fecha-hasta"
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={() => { setFechaDesde(''); setFechaHasta(''); }}
              className="w-full"
              data-testid="purchase-clear-filters"
            >
              <X size={16} className="mr-2" />
              Limpiar Filtros
            </Button>
          </div>
        </div>
      </div>

      {/* Busqueda */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
        <Input
          placeholder="Buscar compras por numero, suplidor, NCF, usuario..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-purchases-input"
        />
      </div>

      {/* Tabla de Compras */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="custom-table">
            <thead>
              <tr>
                <th>No. Factura</th>
                <th>NCF</th>
                <th>Suplidor</th>
                <th>Fecha</th>
                <th>Forma de Pago</th>
                <th>Total</th>
                <th>Usuario</th>
                <th>Adjunto</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody data-testid="purchases-table-body">
              {filteredPurchases.map((purchase) => (
                <tr key={purchase.id} data-testid={`purchase-row-${purchase.id}`}>
                  <td className="font-medium">{purchase.numero_factura}</td>
                  <td className="text-sm text-slate-600">{purchase.ncf || '-'}</td>
                  <td>
                    <div className="font-medium">{purchase.suplidor_nombre || 'N/A'}</div>
                  </td>
                  <td className="text-sm">{formatDateDR(purchase.fecha_factura)}</td>
                  <td>
                    <span className="text-sm capitalize">{purchase.forma_pago}</span>
                  </td>
                  <td className="font-semibold text-green-600">{formatCurrency(purchase.total)}</td>
                  <td className="text-sm">{purchase.usuario_nombre}</td>
                  <td className="text-center">
                    {purchase.adjunto_url ? (
                      <a
                        href={`${API.replace('/api', '')}${purchase.adjunto_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800"
                        data-testid={`purchase-attachment-${purchase.id}`}
                      >
                        <Paperclip size={16} />
                      </a>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewPurchaseDetail(purchase.id)}
                        data-testid={`view-purchase-btn-${purchase.id}`}
                        title="Ver detalles"
                      >
                        <Eye size={16} />
                      </Button>
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeletePurchase(purchase.id)}
                          data-testid={`delete-purchase-btn-${purchase.id}`}
                          title="Eliminar compra"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredPurchases.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Package size={48} className="mx-auto mb-2 opacity-50" />
            <p>No se encontraron compras</p>
          </div>
        )}
      </div>

      {/* Dialog Detalle de Compra */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="purchase-detail-dialog">
          {selectedPurchase && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">Detalle de Compra #{selectedPurchase.numero_factura}</DialogTitle>
                <DialogDescription>
                  {selectedPurchase.ncf ? `NCF: ${selectedPurchase.ncf} | ` : ''}
                  Fecha: {formatDateDR(selectedPurchase.fecha_factura)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Info General */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold">Suplidor:</span> {selectedPurchase.suplidor_nombre || 'N/A'}
                  </div>
                  <div>
                    <span className="font-semibold">No. Factura:</span> {selectedPurchase.numero_factura}
                  </div>
                  <div>
                    <span className="font-semibold">No. Conduce:</span> {selectedPurchase.numero_conduce || 'N/A'}
                  </div>
                  <div>
                    <span className="font-semibold">NCF:</span> {selectedPurchase.ncf || 'N/A'}
                  </div>
                  <div>
                    <span className="font-semibold">Fecha Factura:</span> {formatDateDR(selectedPurchase.fecha_factura)}
                  </div>
                  <div>
                    <span className="font-semibold">Fecha Vencimiento:</span>{' '}
                    {selectedPurchase.fecha_vencimiento ? formatDateDR(selectedPurchase.fecha_vencimiento) : 'N/A'}
                  </div>
                  <div>
                    <span className="font-semibold">Moneda:</span> {selectedPurchase.moneda}
                  </div>
                  <div>
                    <span className="font-semibold">Forma de Pago:</span> {selectedPurchase.forma_pago}
                  </div>
                  <div>
                    <span className="font-semibold">Usuario:</span> {selectedPurchase.usuario_nombre}
                  </div>
                </div>

                {/* Items Table */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Productos Comprados</h4>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2">Producto</th>
                        <th className="text-right p-2">Cantidad</th>
                        <th className="text-right p-2">Costo Unit.</th>
                        <th className="text-right p-2">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPurchase.items.map((item, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-2 font-medium">{item.producto_nombre}</td>
                          <td className="p-2 text-right">{item.cantidad}</td>
                          <td className="p-2 text-right">{formatCurrency(item.costo_unitario)}</td>
                          <td className="p-2 text-right font-semibold">{formatCurrency(item.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totales */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(selectedPurchase.subtotal)}</span>
                  </div>
                  {selectedPurchase.transporte > 0 && (
                    <div className="flex justify-between text-sm text-emerald-700">
                      <span>+ Transporte:</span>
                      <span>{formatCurrency(selectedPurchase.transporte)}</span>
                    </div>
                  )}
                  {selectedPurchase.descuento > 0 && (
                    <div className="flex justify-between text-sm text-red-700">
                      <span>- Descuento:</span>
                      <span>{formatCurrency(selectedPurchase.descuento)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total:</span>
                    <span className="text-green-600">{formatCurrency(selectedPurchase.total)}</span>
                  </div>
                </div>

                {/* Observaciones */}
                {selectedPurchase.observaciones && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2">Observaciones</h4>
                    <div className="bg-slate-50 p-3 rounded-lg text-sm">{selectedPurchase.observaciones}</div>
                  </div>
                )}

                {/* Adjunto */}
                {selectedPurchase.adjunto_url && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2">Archivo Adjunto</h4>
                    {selectedPurchase.adjunto_url.endsWith('.pdf') ? (
                      <a
                        href={`${API.replace('/api', '')}${selectedPurchase.adjunto_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 px-4 py-2 rounded-lg"
                      >
                        <FileText size={20} />
                        <span>Ver PDF adjunto</span>
                      </a>
                    ) : (
                      <div>
                        <img
                          src={`${API.replace('/api', '')}${selectedPurchase.adjunto_url}`}
                          alt="Factura adjunta"
                          className="max-h-64 rounded-lg border border-slate-200 cursor-pointer object-contain"
                          onClick={() => setShowAttachmentPreview(true)}
                          data-testid="purchase-detail-image"
                        />
                        <a
                          href={`${API.replace('/api', '')}${selectedPurchase.adjunto_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline mt-1 inline-block"
                        >
                          Abrir en nueva ventana
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Attachment Full Preview Dialog */}
      {selectedPurchase?.adjunto_url && !selectedPurchase.adjunto_url.endsWith('.pdf') && (
        <Dialog open={showAttachmentPreview} onOpenChange={setShowAttachmentPreview}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Factura Adjunta - {selectedPurchase.numero_factura}</DialogTitle>
            </DialogHeader>
            <div className="flex justify-center">
              <img
                src={`${API.replace('/api', '')}${selectedPurchase.adjunto_url}`}
                alt="Factura adjunta"
                className="max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal Busqueda Avanzada de Productos */}
      <Dialog open={productSearchOpen} onOpenChange={setProductSearchOpen} modal={true}>
        <DialogContent className="max-w-6xl max-h-[85vh] z-[60]" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Busqueda Avanzada de Productos</DialogTitle>
            <DialogDescription>Busque productos por nombre, codigo o categoria</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por nombre, codigo interno, codigo de barras o categoria..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>

            <div className="border rounded-lg max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Cod. Interno</th>
                    <th className="text-left p-2">Producto</th>
                    <th className="text-left p-2">Categoria</th>
                    <th className="text-right p-2">Costo</th>
                    <th className="text-center p-2">Stock</th>
                    <th className="text-center p-2">Accion</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(product => {
                    const enCarrito = cart.find(item => item.producto_id === product.id);
                    return (
                      <tr key={product.id} className="border-b hover:bg-slate-50">
                        <td className="p-2 text-slate-700 font-mono text-xs">{product.codigo_interno || '-'}</td>
                        <td className="p-2">
                          <div className="font-medium text-slate-800">{product.nombre}</div>
                          <div className="text-xs text-slate-500">{product.unidad_medida}</div>
                        </td>
                        <td className="p-2 text-slate-600">{product.categoria || '-'}</td>
                        <td className="p-2 text-right font-semibold">{formatCurrency(product.costo || product.precio_compra || 0)}</td>
                        <td className="p-2 text-center">
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                            {product.existencia}
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <Button
                            type="button"
                            size="sm"
                            data-testid={`add-product-btn-${product.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              addToCart(product);
                              setProductSearchOpen(false);
                              setProductSearchTerm('');
                            }}
                            className="bg-blue-500 hover:bg-blue-600"
                          >
                            {enCarrito ? `Agregar (${enCarrito.cantidad})` : 'Agregar'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredProducts.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No se encontraron productos
                </div>
              )}
            </div>

            <div className="flex justify-between items-center text-sm text-slate-600 bg-slate-50 p-3 rounded">
              <span>Haga clic en un producto para agregarlo</span>
              <span className="font-semibold">{filteredProducts.length} productos encontrados</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Purchases;
